/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useContext } from 'react';
import Media from 'react-media';
import { makeStyles, useMediaQuery } from '@material-ui/core';
import { bool, object } from 'prop-types';
import UAParser from 'ua-parser-js';
import ScrollbarContainerPDPGrid from '../../../../GraphqlCategoryPage/Partials/ScrollbarContainer/ScrollbarContainerPDPGrid';
import { CrossMerch } from '../../../../CrossMerchContainer/CrossMerchContainer';
import useSSRMediaQuery from '../../../../../../helpers/hooks/useSSRMediaQuery';

const userAgent = new UAParser();
const browserInfo = userAgent.getResult();
const browserName = browserInfo.browser.name || '';

const useStyles = makeStyles((theme) => ({
    content: {
        display: 'grid',
        columnGap: '2rem',
        gridGap: '1rem',
        // Desktop Layout
        gridTemplateColumns: '1fr 1fr',
        gridTemplateAreas: '"productImageContainer productSummaryContainer"',
        maxWidth: '1400px',
        margin: '0 auto',
        '@media screen and (max-width: 1279.9px)': {
            gridTemplateColumns: '40% 60%',
        },
        '@media screen and (max-width: 1023.9px)': {
            // Tablet-ish Layout
            gridTemplateColumns: '40% 60%',
        },
        '@media screen and (max-width: 779.9px)': {
            // Tablet-ish Layout
            gridGap: '1em',
        },
        '@media screen and (max-width: 600px)': {
            // Mobile Layout
            gridTemplateColumns: '100%',
            gridTemplateAreas: '"productImageContainer" "productSummaryContainer" "smartGift"',
        },

    },
    productImageContainer: {
        gridArea: 'productImageContainer',

    },

    productImageContent: () => ({
        '@media screen and (min-width: 1279.9px)': {
            width: browserName === 'Safari' ? 'calc(100% - 1rem)' : '100%',
        },
        display: 'grid',
        gridTemplateRows: '1fr auto',
        gridTemplateColumns: '15% 85%',
        gridTemplateAreas: '"productImageAlt productImageGrid" ". smartGift"',
        gap: '1rem',
        '@media only screen and (max-width: 1279.9px)': {
            display: 'grid',
            rowGap: '0.5rem',
            gridTemplateRows: '1fr',
            gridTemplateColumns: '100%',
            gridTemplateAreas: `
               "productImageGrid"
               "productImageAlt"
               "smartGift"
            `,
        },
        '@media only screen and (max-width: 1023.9px)': {
            gap: '1rem',
        },

        '& .altImageButton': {
            display: 'block',
            position: 'relative',
            padding: 0,
            width: '100%',
            height: 'auto',
            border: 'none',
            outline: 'none', // TODO: Need to add outline for accessibility
            cursor: 'pointer',
            '& img': {
                display: 'block',
            },
        },
        '& .altImagesContainer': {
            display: 'grid',
            gridAutoRows: 'min-content',
            rowGap: '1rem',
            gridArea: 'productImageAlt',
            margin: 0,
            gridTemplateColumns: '',
            gridAutoFlow: 'row',
            [theme.breakpoints.down(1280)]: {
                gap: ' 1rem',
                gridAutoFlow: 'column',
            },
        },
        '& .productImage': {
            display: 'grid',
            gridArea: 'productImageGrid',
            margin: 0,
            '@media screen and (max-width: 1023.9px)': {
                maxWidth: '100% !important',
            },
            '@media screen and (max-width: 767.9px)': {
                width: '100%',
                maxWidth: '100% !important',
            },
        },
        '& div[data-testid="smart-gift-ui-desktop"], & div[data-testid="smart-gift-ui-mobile"]': {
            gridArea: 'smartGift',
            margin: '0',
            height: 'fit-content',
            maxWidth: '100%',
            minWidth: 'auto',
        },
        '& div[data-testid="smart-gift-ui-desktop"]': {
            margin: '0',
            '@media screen and (max-width: 1279.9px)': {
                gridColumnStart: 1,
                margin: '0',
            },
        },
        '& div[data-testid="smart-gift-ui-mobile"]': {
            width: '100%',
            maxWidth: '100%',
            margin: '0',
        },
    }),

    productSummaryContainer: {
        position: 'relative',
        gridArea: 'productSummaryContainer',
        justifySelf: 'end',
        width: 'clamp(320px, 100%, 650px)',
        '@media only screen and (max-width: 1280px)': {
            justifySelf: 'center',
            width: 'clamp(320px, 95%, 650px)',
        },
        '@media only screen and (max-width: 1023px)': {
            '& [class*="YotpoProductReview-alignment"]': {
                position: 'relative',
            },
            '& [class*="ProductReview-alignment"]': {
                position: 'relative',
            },
        },
        '@media only screen and (max-width: 1199px)': {
            justifySelf: 'left',
        },
        '@media only screen and (max-width: 767px)': {
            '& .GraphqlFoodProductSkus': {
                display: 'none',
            },
        },
        '@media screen and (max-width: 600px)': {
            // Mobile Layout
            width: '100%',
        },
    },
    productImageContentV3: {
        '@media (min-width: 1025px) and (max-width: 1279.9px)': {
            display: 'grid',
            gridTemplateRows: '1fr auto',
            gridTemplateColumns: '15% 85%',
            gridTemplateAreas: '"productImageAlt productImageGrid" ". smartGift"',
            gap: '1rem',
            rowGap: '0.5rem',
        },
    },
    contentWrapper: {
        '@media only screen and (min-width: 1279.9px)': {
            gridTemplateColumns: '53.65% calc(100% - 53.65%)',
            '& $productImageContent': {
                gridTemplateColumns: '13.6% 84.9%',
            },
            '& $productSummaryContainer': {
                padding: '0px 32px 0px 21px',
                justifySelf: 'flex-start',
            },
        },
        '@media only screen and (max-width: 1199px)  and (min-width:600px)': {
            '& $productSummaryContainer': {
                justifySelf: 'flex-start',
                padding: '0px 16px',
            },
        },
    },
}));

// documentation: https://1800flowersinc.atlassian.net/wiki/spaces/RP/pages/288227337/PDP+Product+Details+Page
const TwoColRedesignResponsiveGrid = (props) => {
    const {
        components,
        isPdpRedesignEnabled,
        isPassportAfterCta,
    } = props;

    const {
        ProductImageUI,
        SmartGift,
        ProductTitle,
        ProductReview,
        GraphqlProductSkus,
        GraphqlAddonContainer,
        SnipeBanner,
        GraphqlAgeVerification,
        SubscriptionProductBannerUI,
        ProductQuantity,
        GraphqlPDPPromoContainerTop,
        GraphqlPDPPromoContainer,
        PmallSummary,
        PmallPersonalization,
        PassportBannerUI,
        PDPAddToCart,
        NBCUMovieBanner,
        PDPDeliveryPopupContainer,
        GoDeluxe,
        ProductDetails,
        GCIPersonalization,
        PromoMessageUI,
        PLAProductIcon,
        ProductTitleTop,
        QandASection,
    } = components;
    const { featureFlags = {} } = useContext(CrossMerch);
    const isAltV3 = featureFlags['is-pdp-alt-v3-enabled'] || false;
    const isZoomEnabled = featureFlags['is-pdp-product-image-zoom-v2-enabled'] || false;
    const isDesktopDevice = useMediaQuery('(min-width: 599px)');
    const adjustTablet = useMediaQuery('(max-width: 767px) and (min-width:600px)');

    const classes = useStyles({ isZoomEnabled });
    const device = useSSRMediaQuery();
    const mediaQueries = {
        small: '(max-width: 599px)',
        medium: '(min-width: 600px) and (max-width: 1199px)',
        large: '(min-width: 1200px)',
    };

    // Power Reviews
    const isPDPReviewsEnabled = featureFlags['is-pdp-reviews-enabled'];
    const isPowerReviewEnabled = featureFlags['is-power-review-enabled'];
    const isPDPYotpoEnabled = featureFlags['is-pdp-yotpo-enabled'];

    // uncomment when needed
    // const variantName = 'TwoColRedesignResponsive'; // this should be unique and never change

    return (
        <>
            <Media queries={mediaQueries} defaultMatches={{ small: device === 'mobile' }}>
                {(matches) => (
                    matches.small && PLAProductIcon()
                )}
            </Media>
            <div
                className={`${classes.content} ${isPdpRedesignEnabled ? classes.contentWrapper : ''}`}
                data-testid="pdp-variant-twocolredesignresponsive-grid"
            >
                {/* PRODUCT IMAGE CONTAINER */}
                <div className={`${classes.productImageContainer} productImageContainer`}>
                    <Media queries={mediaQueries} defaultMatches={{ small: device === 'mobile', medium: device === 'desktop', large: device === 'desktop' }}>
                        {(matches) => (
                            <>
                                {matches.small && (
                                    // render mobile product presentation
                                    <div className={classes.contentLeft}>
                                        {isPdpRedesignEnabled && ProductTitleTop()}
                                        {ProductImageUI('grid')}
                                    </div>
                                )}
                                {(matches.medium || matches.large) && (
                                    // render desktop product presentation
                                    <ScrollbarContainerPDPGrid
                                        contentContainerId="contentRight"
                                        isFoodBrand
                                        type="productBase"
                                    >
                                        <div className={`${classes.productImageContent} ${isAltV3 ? classes.productImageContentV3 : ''}`}>
                                            {ProductImageUI('grid')}
                                            {SmartGift()}
                                        </div>
                                    </ScrollbarContainerPDPGrid>
                                )}
                            </>
                        )}
                    </Media>
                </div>

                {/* PRODUCT SUMMARY */}
                <div className={classes.productSummaryContainer}>
                    <div className={classes.productSummaryContent}>
                        {isPdpRedesignEnabled && adjustTablet && ProductTitleTop()}
                        {((isPdpRedesignEnabled && isDesktopDevice) || !isPdpRedesignEnabled) && ProductTitle()}
                        {!isPdpRedesignEnabled && isPDPReviewsEnabled && isPowerReviewEnabled && ProductReview()}
                        {GraphqlPDPPromoContainerTop()}
                        {GraphqlProductSkus()}
                        {
                            <Media queries={mediaQueries} defaultMatches={{ small: device === 'mobile', medium: device === 'desktop', large: device === 'desktop' }}>
                                {(matches) => (
                                    <>
                                        {matches.small && GoDeluxe(null, true)}
                                        {(matches.medium || matches.large) && GoDeluxe()}
                                    </>
                                )}
                            </Media>
                        }
                        {GraphqlAddonContainer()}
                        {GCIPersonalization()}
                        {SnipeBanner()}
                        {GraphqlAgeVerification()}
                        {SubscriptionProductBannerUI()}
                        {PmallSummary()}
                        {PmallPersonalization()}
                        {GraphqlPDPPromoContainer()}
                        {!isPassportAfterCta && PassportBannerUI()}
                        {ProductQuantity()}
                        {PDPAddToCart()}
                        {isPassportAfterCta && PassportBannerUI()}
                        {NBCUMovieBanner()}
                        {PDPDeliveryPopupContainer()}
                        {PromoMessageUI()}
                        <Media queries={mediaQueries} defaultMatches={{ small: device === 'mobile' }}>
                            {(matches) => (
                                matches.small && SmartGift()
                            )}
                        </Media>
                        {!isPdpRedesignEnabled && ProductDetails()}
                        {QandASection()}
                    </div>
                </div>
            </div>
        </>
    );
};

TwoColRedesignResponsiveGrid.propTypes = {
    components: object.isRequired,
    isPdpRedesignEnabled: bool,
    isPassportAfterCta: bool,
};

TwoColRedesignResponsiveGrid.defaultProps = {
    isPdpRedesignEnabled: false,
    isPassportAfterCta: false,
};
export default TwoColRedesignResponsiveGrid;
